<script setup lang="ts">
import { ReferencingObjectDto } from '@/api/generator';
import { useToggle } from '@vueuse/core';
import { watch } from 'vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import EntityReferencesTable from '@/components/EntityReferencesModal/components/EntityReferencesTable.vue';
import { DialogSize } from '../Dialog/types';

type EntityReferencesModalProps = {
  title: string;
  hideRefTable?: boolean;
  references?: ReferencingObjectDto[] | null;
  size?: DialogSize;
};

const props = withDefaults(defineProps<EntityReferencesModalProps>(), {
  references: null,
  hideRefTable: false,
  size: undefined,
});
const [isDialogOpen, toggleDialog] = useToggle(false);

const emits = defineEmits<{
  dialogOpen: [boolean];
}>();

watch(
  () => props.references,
  () => {
    if (props.references && props.references.length > 0) openDialog();
    else toggleDialog(false);
  },
);

watch(
  () => isDialogOpen.value,
  () => emits('dialogOpen', isDialogOpen.value),
);

function openDialog() {
  toggleDialog(true);
}
</script>

<template>
  <Dialog v-model="isDialogOpen" :size="size ? size : hideRefTable ? 'medium' : 'large'">
    <template #title>
      <h3>{{ title }}</h3>
    </template>
    <template #text>
      <v-row>
        <v-col>
          <p class="text-pre-line">
            <slot name="description">
              {{ $t('EntityReferencesInfoMsg') }}
            </slot>
          </p>
        </v-col>
      </v-row>
      <v-row noGutters>
        <v-col>
          <EntityReferencesTable
            v-if="!hideRefTable"
            :references="references"
            :title="title"
          ></EntityReferencesTable>
        </v-col>
      </v-row>
    </template>

    <template #actions>
      <slot name="actions">
        <SecondaryVBtn class="mr-0" @click="toggleDialog(false)">{{ $t('Close') }}</SecondaryVBtn>
      </slot>
    </template>
  </Dialog>
</template>
